/* eslint-disable jsx-a11y/alt-text */
import { css, media, styled } from '@nx-kit/styling';
import { Theme } from 'themes/themeTypes';
import Container from 'components/layout/Grid/Container';
import Link from 'components/atoms/Link/Link';
import { ImageTextProps } from 'components/organisms/ImageText/ImageText.types';

type PartnerEntry = ImageTextProps['primary'];

type PartnerLogosGridProps = {
  className?: string;
  entries: PartnerEntry[];
};

const Section = styled.div`
  /* Container is a 12-column grid; span the full width. */
  grid-column: 1 / -1;
  margin: 24px 0 80px;
  ${media('md')} {
    margin: 32px 0 120px;
  }
`;

const Grid = styled.div<{ centered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${media('md')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media('lg')} {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }

  /* Short sections (fewer than 3 logos) center their cards instead of leaving
     an empty cell, while keeping the same card width as the 3-up grid. */
  ${({ centered }) =>
    centered &&
    css`
      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      > * {
        flex: 1 1 100%;
      }

      ${media('md')} {
        > * {
          flex: 0 1 calc((100% - 24px) / 2);
          max-width: calc((100% - 24px) / 2);
        }
      }

      ${media('lg')} {
        > * {
          flex: 0 1 calc((100% - 64px) / 3);
          max-width: calc((100% - 64px) / 3);
        }
      }
    `}
`;

const Card = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${({ theme }) => theme.global.color.white};
  border: 1px solid ${({ theme }) => theme.global.color.gray150};
  border-radius: 8px;
  padding: 24px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${media('md')} {
    padding: 32px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.global.color.primary};
    box-shadow: 0 12px 24px -14px rgba(0, 0, 0, 0.3);
  }
`;

// Larger logo area; 16:9 matches the source canvases, logo is contained (never cropped).
const LogoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 60%;
  margin-bottom: 24px;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: left center;
  }
`;

const Subtitle = styled.p<{ theme: Theme }>`
  margin: 0 0 4px;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.global.color.primary};
`;

const Title = styled.p<{ theme: Theme }>`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.global.color.gray900};
`;

const StyledLink = styled(Link)`
  margin-top: auto;
  padding-top: 16px;
`;

const getLogoSrc = (image: PartnerEntry['image']): string => {
  const url = 'url' in image ? image.url : undefined;
  if (!url) {
    return '';
  }
  const clean = url.replace(/[?&](w|h|rect)=[^&]+/g, '');
  return `${clean}${clean.includes('?') ? '&' : '?'}w=640`;
};

export const PartnerLogosGrid: React.FC<PartnerLogosGridProps> = ({ className, entries }) => (
  <Container className={className}>
    <Section>
      <Grid centered={entries.length < 3}>
        {entries.map((entry, index) => {
          const name = entry.headline?.[0]?.text ?? '';
          const src = getLogoSrc(entry.image);
          const hasLink = !!(entry.link?.url || entry.link?.link_type === 'Document');

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Card key={`partner-${index}`}>
              <LogoFrame>{src && <img src={src} alt={name} loading="lazy" />}</LogoFrame>
              {entry.subheadline && <Subtitle>{entry.subheadline}</Subtitle>}
              {name && <Title>{name}</Title>}
              {hasLink && (
                <StyledLink skin="tertiaryBig" url={entry.link}>
                  {entry.link_text || 'Zur Website'}
                </StyledLink>
              )}
            </Card>
          );
        })}
      </Grid>
    </Section>
  </Container>
);

export default PartnerLogosGrid;
